import { reportPageActionToNewRelic } from '@verifime/utils';

/** Which lookup a report is about. */
export type TMetadataLookupSource = 'customer-id' | 'tracking-reference';

const SOURCE_LABEL: Record<TMetadataLookupSource, string> = {
  'customer-id': 'customer ID',
  'tracking-reference': 'tracking reference',
};

/**
 * New Relic page actions, named like `identity-page-eligibility-check-failed` in the customer
 * portal. Each carries `source` (a constant label) and `status` (a reason string): never an id, a
 * token, the error object or anything from a response body.
 */
export const LOOKUP_FAILED_PAGE_ACTION = 'workflow-metadata-lookup-failed';
export const LOOKUP_IGNORED_ITEM_PAGE_ACTION = 'workflow-metadata-lookup-ignored-item';

const UNEXPECTED_ERROR = 'unexpected-error';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The HTTP status the person-api answered with, as text: straight off an axios error, or off the
 * shared Zodios plugin's rewritten 5xx, which has no `response` and carries the status as a numeric
 * `code` (an axios error's own `code` is a string).
 */
function httpStatus({ response, code }: Record<string, unknown>): string | undefined {
  if (isRecord(response) && typeof response.status === 'number') {
    return String(response.status);
  }
  return typeof code === 'number' ? String(code) : undefined;
}

/**
 * A reason a failed lookup is logged and reported by: the HTTP status when the person-api answered
 * with one, `network-error` when the request got no answer, `invalid-response` when the body failed
 * the generated Zod schema (a Zodios error carries the ZodError as its cause), or `unexpected-error`
 * for anything else. Never derived from a message or a body, so it cannot carry an id or a token.
 */
export function failureReason(error: unknown): string {
  if (!isRecord(error)) {
    return UNEXPECTED_ERROR;
  }
  const status = httpStatus(error);
  if (status) {
    return status;
  }
  if (error.isAxiosError === true) {
    return 'network-error';
  }
  return isRecord(error.cause) && error.cause.name === 'ZodError'
    ? 'invalid-response'
    : UNEXPECTED_ERROR;
}

/**
 * Reports a failed lookup: to the console for local visibility, and to New Relic, which does not
 * collect console output in this deployment, so the page action is what engineering sees.
 */
export function reportLookupFailure(source: TMetadataLookupSource, reason: string): void {
  console.error(`Fetching workflow metadata by ${SOURCE_LABEL[source]} failed`, {
    status: reason,
  });
  reportPageActionToNewRelic(LOOKUP_FAILED_PAGE_ACTION, { source, status: reason });
}

/**
 * Reports an item the lookup did not read (an association type it does not support). It is not a
 * failure, and the screen treats it as nothing found, so it is reported on its own.
 */
export function reportIgnoredItem(source: TMetadataLookupSource, associationType: string): void {
  const reason = 'unsupported-association-type';
  console.warn(`Workflow metadata lookup by ${SOURCE_LABEL[source]} ignored an item`, {
    reason,
    associationType,
  });
  reportPageActionToNewRelic(LOOKUP_IGNORED_ITEM_PAGE_ACTION, { source, status: reason });
}
